import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Calendar as CalendarIcon,
  PlusCircle,
  Building2,
  Users,
  IndianRupee,
  RefreshCw,
  Clock,
  ArrowRight,
  ListOrdered
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import AvailabilityCalendar from "@/components/calendar/AvailabilityCalendar";
import BookingsList from "@/components/bookings/BookingsList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export default function CalendarPage({ profile, businessInfo, onRefresh }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_bookings: 0,
    confirmed_count: 0,
    total_revenue: 0,
    total_pending_balance: 0
  });
  const [bookings, setBookings] = useState([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      if (res.data) {
        setStats(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    setIsLoadingBookings(true);
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data || []);
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
    } finally {
      setIsLoadingBookings(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchBookings();
  }, [fetchStats, fetchBookings, refreshKey]);


  const handleSelectDateToBook = (dateStr) => {
    navigate(`/bookings/new?date=${dateStr}`);
  };

  const handleOpenNewBooking = () => {
    navigate("/bookings/new");
  };

  const handleViewBooking = (booking) => {
    navigate(`/bookings/${booking.id}`);
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel / delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      setRefreshKey((k) => k + 1);
      fetchBookings();
      fetchStats();
      if (onRefresh) onRefresh();
    } catch (err) {
      alert("Failed to delete booking.");
    }
  };

  const hallName = businessInfo?.business_name || "Grand Royal Palace & Convention Hall";
  const address = businessInfo?.address || "";
  const capacity = businessInfo?.capacity_guests ? `${businessInfo.capacity_guests} Guests` : "";

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in" data-testid={TEST_IDS.calendarContainer}>
      
      {/* Page Heading & Venue Header */}
      <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">{hallName}</h1>
            <p className="text-xs sm:text-sm text-pink-100 font-medium">
              {address} • Capacity: {capacity} Guests
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenNewBooking}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-pink-950 hover:bg-pink-50 text-xs font-bold transition-all shadow-md"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              <span>+ New Booking</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Metric Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Total Bookings</span>
            <div className="w-7 h-7 rounded-lg bg-pink-50 text-pink-700 flex items-center justify-center">
              <CalendarIcon className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-pink-950" data-testid={TEST_IDS.statTotalBookings}>
            {stats.total_bookings}
          </div>
          <span className="text-[11px] text-gray-400 mt-1 block font-medium">Single-day & Multi-day</span>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Confirmed Events</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-pink-950" data-testid={TEST_IDS.statConfirmedEvents}>
            {stats.confirmed_count}
          </div>
          <span className="text-[11px] text-emerald-600 mt-1 block font-semibold">Active Scheduled</span>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Total Value</span>
            <div className="w-7 h-7 rounded-lg bg-pink-50 text-pink-700 flex items-center justify-center">
              <IndianRupee className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-pink-950 flex items-center" data-testid={TEST_IDS.statTotalRevenue}>
            <span>₹</span>
            <span>{(stats.total_revenue || 0).toLocaleString("en-IN")}</span>
          </div>
          <span className="text-[11px] text-gray-400 mt-1 block font-medium">Revenue Volume</span>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Pending Balance</span>
            <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-pink-950 flex items-center" data-testid={TEST_IDS.statPendingBalance}>
            <span>₹</span>
            <span>{(stats.total_pending_balance || 0).toLocaleString("en-IN")}</span>
          </div>
          <span className="text-[11px] text-amber-600 mt-1 block font-semibold">Due to Collect</span>
        </div>
      </div>

      {/* Full-Page Availability Calendar Matrix */}
      <AvailabilityCalendar
        onSelectDateToBook={handleSelectDateToBook}
        onViewBooking={handleViewBooking}
        refreshKey={refreshKey}
      />

      {/* Convention Hall Bookings List directly below on the same page */}
      <div className="pt-6 border-t border-pink-200/80 space-y-4" data-testid="bookings-section-container">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-pink-100 text-pink-900 flex items-center justify-center font-bold">
              <ListOrdered className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-pink-950">Convention Hall Bookings</h2>
              <p className="text-xs text-gray-500 font-medium">All single-day and multi-day reservations</p>
            </div>
          </div>
        </div>

        <BookingsList
          bookings={bookings}
          businessInfo={businessInfo}
          isLoading={isLoadingBookings}
          onViewBooking={handleViewBooking}
          onDeleteBooking={handleDeleteBooking}
          onOpenNewBooking={handleOpenNewBooking}
        />
      </div>

    </div>
  );
}
